import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type SidebarStyle = 'light' | 'colored' | 'dark';
export type NavStyle = 'flat' | 'grouped';

interface UIStore {
  darkMode: boolean;
  sidebarCollapsed: boolean;
  mobileSidebarOpen: boolean;
  commandPaletteOpen: boolean;
  groupPaletteKey: string;
  fullPaletteMode: boolean;
  showPageHelp: boolean;
  sidebarStyle: SidebarStyle;
  navStyle: NavStyle;
  toggleDarkMode: () => void;
  toggleSidebar: () => void;
  toggleMobileSidebar: () => void;
  setMobileSidebarOpen: (open: boolean) => void;
  setCommandPaletteOpen: (open: boolean) => void;
  setGroupPaletteKey: (key: string) => void;
  setFullPaletteMode: (full: boolean) => void;
  setShowPageHelp: (show: boolean) => void;
  setSidebarStyle: (style: SidebarStyle) => void;
  setNavStyle: (style: NavStyle) => void;
}

export const useUIStore = create<UIStore>()(
  persist(
    (set) => ({
      darkMode: false,
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      commandPaletteOpen: false,
      groupPaletteKey: 'etg',
      fullPaletteMode: false,
      showPageHelp: true,
      sidebarStyle: 'light' as SidebarStyle,
      navStyle: 'flat' as NavStyle,
      toggleDarkMode: () => set((state) => ({ darkMode: !state.darkMode })),
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      toggleMobileSidebar: () => set((state) => ({ mobileSidebarOpen: !state.mobileSidebarOpen })),
      setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),
      setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
      setGroupPaletteKey: (key) => set({ groupPaletteKey: key }),
      setFullPaletteMode: (full) => set({ fullPaletteMode: full }),
      setShowPageHelp: (show) => set({ showPageHelp: show }),
      setSidebarStyle: (style) => set({ sidebarStyle: style }),
      setNavStyle: (style) => set({ navStyle: style }),
    }),
    {
      name: 'etg-ui-store',
      // mobileSidebarOpen is ephemeral — never persist (would re-open drawer on next load)
      partialize: (state) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { mobileSidebarOpen: _ignored, ...rest } = state;
        return rest;
      },
    }
  )
);
